import { useState } from "react";
import { ChatCircleText, SealCheck, Star } from "@phosphor-icons/react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

export const Stars = ({ rating, testId = "star-rating" }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 text-[#C26B51]">
    {[1, 2, 3, 4, 5].map((star) => <Star key={star} size={16} weight={star <= rating ? "fill" : "regular"} />)}
  </span>
);

export const ReviewThread = ({ review, currentUser, onUpdated }) => {
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const canComment = currentUser?.id === review.buyer_id || currentUser?.id === review.seller_id;

  const submitComment = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/reviews/${review.id}/comments`, { body });
      setBody("");
      onUpdated?.(data);
      toast.success("Comment added");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add comment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <article data-testid={`review-thread-${review.id}`} className="border border-[#D5D1C4] bg-[#F1EFE7] p-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row">
        <div>
          <Stars rating={review.rating} testId={`review-stars-${review.id}`} />
          <span data-testid={`review-packaging-stars-${review.id}`} className="ml-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.14em] text-[#5C6A61]">Packaging <Stars rating={review.packaging_rating || review.rating} testId={`review-packaging-rating-${review.id}`} /></span>
          <h3 data-testid={`review-buyer-${review.id}`} className="mt-2 font-serif text-2xl text-[#1A3626]">Review from {review.buyer_name}</h3>
          {review.verified_purchase && <span data-testid={`verified-purchase-badge-${review.id}`} className="mt-2 inline-flex items-center gap-1 border border-[#C26B51] px-2 py-1 text-xs uppercase tracking-[0.14em] text-[#C26B51]"><SealCheck size={14} weight="duotone" /> Verified purchase</span>}
        </div>
        <p data-testid={`review-seller-${review.id}`} className="text-xs uppercase tracking-[0.16em] text-[#5C6A61]">Seller: {review.seller_name}</p>
      </div>
      <p data-testid={`review-body-${review.id}`} className="mt-4 leading-7 text-[#1A3626]">{review.body}</p>
      <div data-testid={`review-comments-${review.id}`} className="mt-5 space-y-3">
        {review.comments?.map((comment) => <div data-testid={`review-comment-${comment.id}`} key={comment.id} className="border-l border-[#C26B51] pl-4 text-sm"><p className="font-semibold text-[#1A3626]">{comment.author_name} · {comment.author_role}</p><p className="mt-1 text-[#5C6A61]">{comment.body}</p></div>)}
      </div>
      {canComment && <form data-testid={`review-comment-form-${review.id}`} onSubmit={submitComment} className="mt-5 grid gap-3"><Textarea data-testid={`review-comment-input-${review.id}`} required minLength={2} className="rounded-none border-[#D5D1C4]" placeholder="Reply to this review" value={body} onChange={(e) => setBody(e.target.value)} /><Button data-testid={`review-comment-submit-${review.id}`} disabled={loading} className="w-fit rounded-none bg-[#1A3626] uppercase tracking-[0.16em]"><ChatCircleText size={17} /> Comment</Button></form>}
    </article>
  );
};